import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PaymentGateway, PaymentGatewayRequest } from "../../entry/server/app.ts";
import { appRepo } from "../../repo/app-repo.ts";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { AppContext } from "../../context/app-context.tsx";
import { ASSETS_BASE_URL } from "../../utils/constants.ts";

export default function EditGateway() {
  const navigate = useNavigate();
  const { id } = useParams();
  const gatewayId = Number(id);

  const [form, setForm] = useState({
    name: "",
    slug: "",
    key: "",
    is_active: true,
    active_icon: null as File | null,
    inactive_icon: null as File | null,
  });
  const [currentIcons, setCurrentIcons] = useState({
    active_icon: "",
    inactive_icon: "",
  });
  const [activeIconError, setActiveIconError] = useState("");
  const [inactiveIconError, setInactiveIconError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { setMessage } = useContext(AppContext);

  const isFormValid = useMemo(() => {
    return Boolean(form.name.trim() && form.slug.trim() && form.key.trim() && (form.active_icon || currentIcons.active_icon) && (form.inactive_icon || currentIcons.inactive_icon));
  }, [form, currentIcons]);
  const activeIconPreview = useMemo(() => (form.active_icon ? URL.createObjectURL(form.active_icon) : currentIcons.active_icon), [form.active_icon, currentIcons.active_icon]);
  const inactiveIconPreview = useMemo(() => (form.inactive_icon ? URL.createObjectURL(form.inactive_icon) : currentIcons.inactive_icon), [form.inactive_icon, currentIcons.inactive_icon]);

  useEffect(() => {
    return () => {
      if (form.active_icon && activeIconPreview) {
        URL.revokeObjectURL(activeIconPreview);
      }
    };
  }, [form.active_icon, activeIconPreview]);

  useEffect(() => {
    return () => {
      if (form.inactive_icon && inactiveIconPreview) {
        URL.revokeObjectURL(inactiveIconPreview);
      }
    };
  }, [form.inactive_icon, inactiveIconPreview]);

  useEffect(() => {
    if (!gatewayId) {
      setMessage({ type: "error", message: "شناسه درگاه معتبر نیست." });
      navigate("/admin/gateways");
      return;
    }

    const fetchGateway = async () => {
      setIsLoading(true);
      try {
        const response = await appRepo.getGateway(gatewayId);
        if (!response.status || !response.data) {
          setMessage({ type: "error", message: response.message || "دریافت اطلاعات درگاه ناموفق بود." });
          navigate("/admin/gateways");
          return;
        }

        const data: PaymentGateway = response.data;
        setForm({
          name: String(data.name),
          slug: String(data.slug),
          key: String(data.key),
          is_active: Boolean(data.is_active),
          active_icon: null,
          inactive_icon: null,
        });

        let activeIcon = data.active_icon.includes("http") ? data.active_icon : `${ASSETS_BASE_URL}${data.active_icon}`;
        let inactiveIcon = data.inactive_icon.includes("http") ? data.inactive_icon : `${ASSETS_BASE_URL}${data.inactive_icon}`;
        setCurrentIcons({
          active_icon: activeIcon,
          inactive_icon: inactiveIcon,
        });
      } catch {
        setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
        navigate("/admin/gateways");
      } finally {
        setIsLoading(false);
      }
    };

    fetchGateway();
  }, [gatewayId, navigate, setMessage]);

  const onChangeField = (field: "name" | "slug" | "key", value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onChangeIcon = (event: any, field: "active_icon" | "inactive_icon") => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const isValidType = file.type === "image/png" || file.type === "image/jpeg";
    if (!isValidType) {
      if (field === "active_icon") {
        setActiveIconError("فقط فایل‌های PNG یا JPG قابل قبول هستند.");
      } else {
        setInactiveIconError("فقط فایل‌های PNG یا JPG قابل قبول هستند.");
      }
      return;
    }

    if (field === "active_icon") {
      setActiveIconError("");
    } else {
      setInactiveIconError("");
    }

    setForm((prev) => ({ ...prev, [field]: file }));
  };

  const onSubmit = async (event: any) => {
    event.preventDefault();

    if (!isFormValid || !gatewayId) {
      setMessage({ type: "error", message: "لطفا همه فیلدها را کامل کنید." });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: PaymentGatewayRequest = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        key: form.key.trim(),
        is_active: form.is_active,
        active_icon: form.active_icon,
        inactive_icon: form.inactive_icon,
      };

      const response = await appRepo.updateGateway(gatewayId, payload);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "ویرایش درگاه با خطا مواجه شد." });
        return;
      }

      setMessage({ type: "success", message: "درگاه با موفقیت ویرایش شد." });
      navigate("/admin/gateways");
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. لطفا دوباره تلاش کنید." });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <section className="space-y-4">
        <AdminPageTitle title="ویرایش درگاه" />
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت اطلاعات درگاه...
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <AdminPageTitle title="ویرایش درگاه" />
      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
        <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
          <label className="space-y-2">
            <span className="text-sm text-slate-300">نام درگاه</span>
            <input
              value={form.name}
              onChange={(event) => onChangeField("name", event.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
              placeholder="مثال: زرین پال"
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm text-slate-300">اسلاگ</span>
            <input
              value={form.slug}
              onChange={(event) => onChangeField("slug", event.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
              placeholder="zarinpal"
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm text-slate-300">کلید درگاه</span>
            <input
              value={form.key}
              onChange={(event) => onChangeField("key", event.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
              placeholder="gateway-key"
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm text-slate-300">تصویر حالت فعال (PNG/JPG)</span>
            <input
              type="file"
              accept=".png,.jpg,.jpeg,image/png,image/jpeg"
              onChange={(event) => onChangeIcon(event, "active_icon")}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 file:me-3 file:rounded-md file:border-0 file:bg-indigo-500 file:px-3 file:py-1 file:text-white hover:file:bg-indigo-400"
            />
            {activeIconError && <p className="text-xs text-rose-300">{activeIconError}</p>}
          </label>

          <label className="space-y-2">
            <span className="text-sm text-slate-300">تصویر حالت غیرفعال (PNG/JPG)</span>
            <input
              type="file"
              accept=".png,.jpg,.jpeg,image/png,image/jpeg"
              onChange={(event) => onChangeIcon(event, "inactive_icon")}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 file:me-3 file:rounded-md file:border-0 file:bg-indigo-500 file:px-3 file:py-1 file:text-white hover:file:bg-indigo-400"
            />
            {inactiveIconError && <p className="text-xs text-rose-300">{inactiveIconError}</p>}
          </label>

          <label className="flex items-center gap-2 md:col-span-2">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
              className="h-4 w-4 accent-indigo-500"
            />
            <span className="text-sm text-slate-300">درگاه فعال باشد</span>
          </label>

          {(activeIconPreview || inactiveIconPreview) && (
            <div className="grid gap-4 md:col-span-2 md:grid-cols-2">
              {activeIconPreview && (
                <div>
                  <p className="mb-2 text-sm text-slate-300">پیش‌نمایش تصویر فعال</p>
                  <img src={activeIconPreview} alt="gateway active preview" className="h-14 w-14 rounded-lg border border-slate-700 object-cover" />
                </div>
              )}
              {inactiveIconPreview && (
                <div>
                  <p className="mb-2 text-sm text-slate-300">پیش‌نمایش تصویر غیرفعال</p>
                  <img src={inactiveIconPreview} alt="gateway inactive preview" className="h-14 w-14 rounded-lg border border-slate-700 object-cover" />
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-3 md:col-span-2">
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "در حال ذخیره..." : "ذخیره تغییرات"}
            </button>
            <Link
              to="/admin/gateways"
              className="inline-flex rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300"
            >
              انصراف
            </Link>
          </div>
        </form>
      </div>
    </section>
  );
}
